import type { BoardBackground, BoardMeta, Folder, SepiaElement, Snapshot, UserInfo } from "../../shared/model";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown, raw?: Blob): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: {
        "x-sepia": "1",
        ...(raw ? { "content-type": raw.type || "application/octet-stream" } : body !== undefined ? { "content-type": "application/json" } : {})
      },
      body: raw ?? (body !== undefined ? JSON.stringify(body) : undefined)
    });
  } catch {
    throw new ApiError(0, "You seem to be offline.");
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string } | null)?.error || `Request failed (${res.status})`);
  return data as T;
}

/** The password never leaves the browser: this is what we send instead (PBKDF2-SHA256, salted with the e-mail). */
export async function deriveKey(password: string, email: string): Promise<string> {
  const enc = new TextEncoder();
  const base = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: enc.encode("sepia/v1|" + email.trim().toLowerCase()), iterations: 310000 },
    base,
    256
  );
  const u8 = new Uint8Array(bits);
  let s = "";
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const api = {
  me: () => call<{ user: UserInfo | null }>("GET", "/api/auth/me"),
  signup: (email: string, name: string, key: string) => call<{ user: UserInfo; recoveryKey: string }>("POST", "/api/auth/signup", { email, name, key }),
  login: (email: string, key: string) => call<{ user: UserInfo }>("POST", "/api/auth/login", { email, key }),
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout", {}),
  recover: (email: string, recoveryKey: string, key: string) => call<{ user: UserInfo; recoveryKey: string }>("POST", "/api/auth/recover", { email, recoveryKey, key }),
  changePassword: (oldKey: string, newKey: string) => call<{ ok: true }>("POST", "/api/auth/password", { oldKey, newKey }),
  newRecoveryKey: (key: string) => call<{ recoveryKey: string }>("POST", "/api/auth/recovery-key", { key }),
  updateProfile: (patch: { name?: string; settings?: Record<string, unknown> }) => call<{ user: UserInfo }>("PATCH", "/api/auth/profile", patch),
  deleteAccount: (key: string) => call<{ ok: true }>("POST", "/api/auth/delete", { key }),

  boards: () => call<{ boards: BoardMeta[] }>("GET", "/api/boards"),
  board: (id: string) => call<{ board: BoardMeta }>("GET", `/api/boards/${id}`),
  createBoard: (b: { title?: string; bg?: BoardBackground; folderId?: string | null; els?: SepiaElement[]; thumb?: string | null }) => call<{ board: BoardMeta }>("POST", "/api/boards", b),
  patchBoard: (id: string, patch: Record<string, unknown>) => call<{ board: BoardMeta }>("PATCH", `/api/boards/${id}`, patch),
  deleteBoard: (id: string) => call<{ ok: true }>("DELETE", `/api/boards/${id}`),
  duplicateBoard: (id: string) => call<{ board: BoardMeta }>("POST", `/api/boards/${id}/duplicate`, {}),
  share: (id: string, mode: "view" | "edit" | null) => call<{ shareMode: BoardMeta["shareMode"]; shareToken: string | null }>("POST", `/api/boards/${id}/share`, { mode }),
  snapshot: (id: string) => call<Snapshot>("GET", `/api/boards/${id}/snapshot`),

  shared: (token: string) => call<{ board: BoardMeta }>("GET", `/api/shared/${token}`),
  sharedSnapshot: (token: string) => call<Snapshot>("GET", `/api/shared/${token}/snapshot`),

  folders: () => call<{ folders: Folder[] }>("GET", "/api/folders"),
  createFolder: (name: string) => call<{ folder: Folder }>("POST", "/api/folders", { name }),
  patchFolder: (id: string, patch: { name?: string; color?: string | null; sort?: number }) => call<{ ok: true }>("PATCH", `/api/folders/${id}`, patch),
  deleteFolder: (id: string) => call<{ ok: true }>("DELETE", `/api/folders/${id}`),

  search: (q: string) => call<{ results: { id: string; title: string; snippet: string; updatedAt: number }[] }>("GET", `/api/search?q=${encodeURIComponent(q)}`),
  graph: () => call<{ nodes: { id: string; title: string; folderId: string | null; updatedAt: number; size: number }[]; links: [string, string][] }>("GET", "/api/graph"),

  uploadAsset: (blob: Blob) => call<{ id: string; mime: string; size: number }>("POST", "/api/assets", undefined, blob),

  sendTouchReport: (report: Record<string, unknown>) => call<{ ok: true; id: string }>("POST", "/api/diag", report)
};

export function assetUrl(src: string): string {
  if (/^[a-z0-9]{24}$/.test(src)) return `/api/assets/${src}`;
  return src;
}
